import {
    ArrowBack,
    ContentCopyOutlined,
    HistoryOutlined,
    Inventory2Outlined,
    NotificationsActiveOutlined,
    NotificationsOffOutlined,
    PeopleOutline,
    PrintOutlined,
    QrCode2Outlined,
    RefreshOutlined,
    RestaurantOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { QRCodeSVG } from "qrcode.react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getErrorMessage,
    getStaffOrder,
    listMenuAvailability,
    listCustomers,
    listQrTables,
    listStaffOrders,
    rejectOrder,
    setMenuItemAvailability,
} from "../api";
import {
    announceStorefrontOrder,
    isStorefrontChimeEnabled,
    playSmoothStorefrontChime,
    setStorefrontChimeEnabled,
} from "./storefrontChime";

const amount = (value) => Number(value || 0).toFixed(2);

export default function RestaurantOnlineOrdersPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [tab, setTab] = useState("pending");
    const [search, setSearch] = useState("");
    const [selectedName, setSelectedName] = useState("");
    const [rejectReason, setRejectReason] = useState("");
    const [chimeEnabled, setChimeEnabled] = useState(isStorefrontChimeEnabled);
    const polledOrderNames = useRef(null);
    const status = tab === "pending" ? "Pending Confirmation" : tab === "history" ? "History" : undefined;
    const ordersQuery = useQuery(
        ["restaurantStorefrontStaff", "orders", status, search],
        () => listStaffOrders({ status, search }),
        { enabled: tab === "pending" || tab === "history", refetchInterval: tab === "pending" ? 10000 : false }
    );
    const customersQuery = useQuery(
        ["restaurantStorefrontStaff", "customers", search],
        () => listCustomers(search),
        { enabled: tab === "customers" }
    );
    const qrQuery = useQuery(
        ["restaurantStorefrontStaff", "qr"],
        listQrTables,
        { enabled: tab === "qr" }
    );
    const availabilityQuery = useQuery(
        ["restaurantStorefrontStaff", "menuAvailability", search],
        () => listMenuAvailability(search),
        { enabled: tab === "availability" }
    );
    const activeListQuery =
        tab === "customers"
            ? customersQuery
            : tab === "qr"
              ? qrQuery
              : tab === "availability"
                ? availabilityQuery
                : ordersQuery;
    const detailQuery = useQuery(
        ["restaurantStorefrontStaff", "order", selectedName],
        () => getStaffOrder(selectedName),
        { enabled: !!selectedName }
    );
    const rejectMutation = useMutation(rejectOrder, {
        onSuccess: () => {
            enqueueSnackbar("Order rejected", { variant: "success" });
            setSelectedName("");
            setRejectReason("");
            queryClient.invalidateQueries(["restaurantStorefrontStaff", "orders"]);
        },
        onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: "error" }),
    });
    const availabilityMutation = useMutation(setMenuItemAvailability, {
        onSuccess: () => {
            queryClient.invalidateQueries(["restaurantStorefrontStaff", "menuAvailability"]);
            queryClient.invalidateQueries(["restaurantStorefront", "catalog"]);
        },
        onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: "error" }),
    });
    const pendingCount = useMemo(
        () => (ordersQuery.data || []).filter((order) => order.status === "Pending Confirmation").length,
        [ordersQuery.data]
    );
    useEffect(() => {
        if (tab !== "pending" || !ordersQuery.data) return;
        const currentNames = new Set(
            ordersQuery.data
                .filter((order) => order.status === "Pending Confirmation")
                .map((order) => order.name)
                .filter(Boolean)
        );
        if (polledOrderNames.current) {
            currentNames.forEach((name) => {
                if (!polledOrderNames.current.has(name)) void announceStorefrontOrder(name);
            });
        }
        polledOrderNames.current = currentNames;
    }, [ordersQuery.data, tab]);

    const openInPos = (order) => {
        navigate(`/pos/${encodeURIComponent(order.table)}/${encodeURIComponent(order.name)}`);
    };
    const copy = async (value) => {
        await navigator.clipboard.writeText(value);
        enqueueSnackbar("Link copied", { variant: "success" });
    };
    const toggleChime = async () => {
        const next = !chimeEnabled;
        setStorefrontChimeEnabled(next);
        setChimeEnabled(next);
        if (next) await playSmoothStorefrontChime({ force: true });
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f7f7f9", py: 3 }}>
            <Container maxWidth="xl">
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={2} sx={{ mb: 3 }}>
                    <Box>
                        <Button startIcon={<ArrowBack />} onClick={() => navigate("/tables")}>Back to POS</Button>
                        <Typography variant="h3" sx={{ fontWeight: 950 }}>Online restaurant orders</Typography>
                        <Typography color="text.secondary">Confirm draft orders, review customer history, and manage table QR codes.</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            startIcon={chimeEnabled ? <NotificationsActiveOutlined /> : <NotificationsOffOutlined />}
                            color={chimeEnabled ? "primary" : "inherit"}
                            variant="outlined"
                            onClick={toggleChime}
                        >
                            Order sound {chimeEnabled ? "on" : "off"}
                        </Button>
                        <Button startIcon={<RefreshOutlined />} variant="outlined" onClick={() => {
                            ordersQuery.refetch(); customersQuery.refetch(); qrQuery.refetch(); availabilityQuery.refetch();
                        }}>Refresh</Button>
                    </Stack>
                </Stack>
                <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
                    <Tabs value={tab} onChange={(_, value) => { setTab(value); setSearch(""); }} variant="scrollable">
                        <Tab value="pending" icon={<RestaurantOutlined />} iconPosition="start" label={`Pending${tab === "pending" ? ` (${pendingCount})` : ""}`} />
                        <Tab value="history" icon={<HistoryOutlined />} iconPosition="start" label="Order history" />
                        <Tab value="customers" icon={<PeopleOutline />} iconPosition="start" label="Customers" />
                        <Tab value="availability" icon={<Inventory2Outlined />} iconPosition="start" label="Menu availability" />
                        <Tab value="qr" icon={<QrCode2Outlined />} iconPosition="start" label="Table QR codes" />
                    </Tabs>
                    <Divider />
                    <Box sx={{ p: 3 }}>
                        {tab !== "qr" ? <TextField fullWidth value={search} onChange={(event) => setSearch(event.target.value)} placeholder={tab === "availability" ? "Search menu items" : "Search by order, customer, mobile, address, or table"} sx={{ mb: 3 }} /> : null}
                        {activeListQuery.isLoading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress /></Box> : null}
                        {activeListQuery.isError ? <Alert severity="error">{getErrorMessage(activeListQuery.error)}</Alert> : null}
                        {(tab === "pending" || tab === "history") && !ordersQuery.isLoading ? (
                            <Grid container spacing={2}>
                                {(ordersQuery.data || []).map((order) => (
                                    <Grid item xs={12} md={6} lg={4} key={order.name}>
                                        <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                                            <CardContent>
                                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                    <Box><Typography sx={{ fontWeight: 900 }}>{order.name}</Typography><Typography variant="body2" color="text.secondary">{order.customer_name} · {order.mobile_no || "No mobile"}</Typography></Box>
                                                    <Chip size="small" label={order.status} color={order.status === "Pending Confirmation" ? "warning" : order.status === "Rejected" ? "error" : "success"} />
                                                </Stack>
                                                <Typography sx={{ mt: 2 }}>{order.order_mode} · {order.table_description}</Typography>
                                                <Typography variant="h6" sx={{ mt: 1, fontWeight: 900 }}>{amount(order.total)}</Typography>
                                                <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={() => setSelectedName(order.name)}>Review order</Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                                {!ordersQuery.data?.length ? <Grid item xs={12}><Alert severity="info">No orders found.</Alert></Grid> : null}
                            </Grid>
                        ) : null}
                        {tab === "customers" && !customersQuery.isLoading ? (
                            <Grid container spacing={2}>
                                {(customersQuery.data || []).map((customer) => (
                                    <Grid item xs={12} md={6} lg={4} key={customer.customer}>
                                        <Card variant="outlined"><CardContent><Typography variant="h6" sx={{ fontWeight: 900 }}>{customer.customer_name}</Typography><Typography color="text.secondary">{customer.mobile_no || customer.website_user}</Typography><Stack direction="row" spacing={1} sx={{ mt: 2 }}><Chip label={`${customer.orders_count} orders`} /><Chip label={`${amount(customer.total_spend)} total`} /></Stack><Button sx={{ mt: 2 }} onClick={() => { setTab("history"); setSearch(customer.customer); }}>View order history</Button></CardContent></Card>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : null}
                        {tab === "availability" && !availabilityQuery.isLoading ? (
                            <>
                                <Alert severity="info" sx={{ mb: 3 }}>Unavailable items remain visible to customers but cannot be added to a cart or submitted in an order.</Alert>
                                <Grid container spacing={2}>
                                    {(availabilityQuery.data || []).map((item) => (
                                        <Grid item xs={12} sm={6} lg={4} key={item.item_code}>
                                            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                                <CardContent>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontWeight: 900 }} noWrap>{item.item_name}</Typography>
                                                            <Typography variant="body2" color="text.secondary" noWrap>{item.item_group} · {item.item_code}</Typography>
                                                        </Box>
                                                        <Switch
                                                            checked={item.available !== false}
                                                            disabled={availabilityMutation.isLoading}
                                                            inputProps={{ "aria-label": `Make ${item.item_name} available` }}
                                                            onChange={(_, available) => availabilityMutation.mutate({ itemCode: item.item_code, available })}
                                                        />
                                                    </Stack>
                                                    <Chip sx={{ mt: 2 }} size="small" color={item.available === false ? "default" : "success"} label={item.available === false ? "Unavailable" : "Available"} />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                    {!availabilityQuery.data?.length ? <Grid item xs={12}><Alert severity="info">No storefront menu items found.</Alert></Grid> : null}
                                </Grid>
                            </>
                        ) : null}
                        {tab === "qr" && !qrQuery.isLoading ? (
                            <>
                                <Alert severity="info" sx={{ mb: 3 }}>QR links use this tenant's current base URL and open the menu with the selected table attached.</Alert>
                                <Grid container spacing={3}>
                                    {(qrQuery.data?.tables || []).filter((table) => table.enabled).map((table) => (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={table.name}>
                                            <Card variant="outlined" sx={{ borderRadius: 3, textAlign: "center" }}><CardContent>
                                                <Box className="restaurant-qr" sx={{ bgcolor: "white", p: 2, display: "inline-flex" }}><QRCodeSVG value={table.url} size={190} level="M" includeMargin /></Box>
                                                <Typography variant="h6" sx={{ fontWeight: 900 }}>{table.description}</Typography><Typography variant="caption" color="text.secondary">{table.room}</Typography>
                                                <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}><Button startIcon={<ContentCopyOutlined />} onClick={() => copy(table.url)}>Copy</Button><Button startIcon={<PrintOutlined />} onClick={() => window.print()}>Print</Button></Stack>
                                            </CardContent></Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </>
                        ) : null}
                    </Box>
                </Paper>
            </Container>
            <Dialog open={!!selectedName} onClose={() => setSelectedName("")} fullWidth maxWidth="sm">
                <DialogTitle>Review {selectedName}</DialogTitle>
                <DialogContent dividers>
                    {detailQuery.isLoading ? <CircularProgress /> : detailQuery.data ? <Stack spacing={2}>
                        <Box>
                            <Typography sx={{ fontWeight: 900 }}>{detailQuery.data.customer_name}</Typography>
                            <Typography color="text.secondary">{detailQuery.data.mobile_no || "No mobile"} · {detailQuery.data.order_mode} · {detailQuery.data.table_description}</Typography>
                            <Typography sx={{ mt: 0.5 }}>{detailQuery.data.delivery_address || "No delivery address"}</Typography>
                        </Box>
                        <Divider />
                        {detailQuery.data.items.map((item) => <Box key={item.item_code}><Typography sx={{ fontWeight: 800 }}>{item.qty} × {item.item_name}</Typography>{item.addons.map((addon) => <Typography key={`${addon.bundle_category}-${addon.item_code}`} variant="body2" color="text.secondary">{addon.bundle_category ? `${addon.bundle_category}: ` : "+ "}{addon.item_name || addon.item_code}</Typography>)}{item.notes ? <Typography variant="body2">Note: {item.notes}</Typography> : null}</Box>)}
                        {detailQuery.data.notes ? <Alert severity="info">{detailQuery.data.notes}</Alert> : null}
                        {detailQuery.data.status === "Pending Confirmation" ? <TextField multiline minRows={2} label="Rejection reason (optional)" value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} /> : null}
                    </Stack> : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedName("")}>Close</Button>
                    {detailQuery.data?.status === "Pending Confirmation" ? <Button color="error" onClick={() => rejectMutation.mutate({ orderName: detailQuery.data.name, reason: rejectReason })}>Reject</Button> : null}
                    {detailQuery.data?.status === "Pending Confirmation" ? <Button variant="contained" onClick={() => openInPos(detailQuery.data)}>Open in POS to confirm</Button> : null}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
